<template>
  <div class="app-container">
    <div id="global-header-placeholder">
      <!-- insert your global header here -->
      <!-- end global header-->
    </div>
    <div class="loader-container">
      <PbsLoader v-show="showLoader" />
    </div>
    <main class="main-content">
      <vl-page v-vl-spacer:bottom.medium>
        <vl-grid v-if="data.isLoggingOut" class="logging-out">
          <span>{{ $t('app.loggingOut') }}</span>
          <vl-loader />
        </vl-grid>
        <template v-else>
          <PbsEnvironmentHeader />
          <PbsImageHeader v-if="isGuest && data.mounted" />
          <Navigation />
          <PbsAlert />
          <vl-main>
            <router-view />
          </vl-main>
          <PbsModal />
        </template>
      </vl-page>
    </main>
    <div id="global-footer-placeholder">
      <!-- insert your global footer here -->
      <!-- end global footer -->
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as widgets from '@govflanders/vl-widget-client';
import { config } from './common/config/config';
import { onMounted, computed, reactive } from 'vue';
import { stores } from './store';
import { useHead } from '@vueuse/head';
import PbsAlert from '@/modules/core/components/alerts/PbsAlert.vue';
import PbsModal from '@/modules/core/components/modals/PbsModal.vue';
import Navigation from './components/Navigation.vue';
import PbsLoader from './components/PbsLoader.vue';
import PbsImageHeader from './components/PbsImageHeader.vue';
import PbsEnvironmentHeader from './components/PbsEnvironmentHeader.vue';
import { usePageStore } from './store/page.store';
import { useUserStore } from '@/store/user.store';

const pageStore = usePageStore();
const userStore = useUserStore();

const data = reactive({
  isLoggingOut: false,
  mounted: false
});

const showLoader = computed(() => {
  return isLoading.value && data.mounted;
});

const isLoading = computed(() => {
  return pageStore.isLoading;
});

const isGuest = computed(() => {
  return !userStore.isAuthenticated;
});

onMounted(async () => {
  addMetadata();
  await mountWidgets();
  await addWidgetHooks();
  data.mounted = true;
});

const addMetadata = () => {
  useHead({
    meta: [
      { name: 'description', content: 'DataPublicatie | Vlaanderen.be' },
      { name: 'pbs_version', content: config.version }
    ]
  });
};

const mountWidgets = async () => {
  try {
    await widgets.bootstrap(config.header.widgetUrl).then((widget) => {
      if (!document.getElementById('headerWrapperElement')) {
        const headerWrapperElement = document.createElement('div');
        headerWrapperElement.id = 'headerWrapperElement';
        const parentContainer = document.getElementById('global-header-placeholder');
        widget.setMountElement(headerWrapperElement);
        widget.mount();
        parentContainer?.insertBefore(headerWrapperElement, parentContainer.childNodes[0] || null);
      }
    });
    await widgets.bootstrap(config.footer.widgetUrl).then((widget) => {
      if (!document.getElementById('footerWrapperElement')) {
        const footerWrapperElement = document.createElement('div');
        footerWrapperElement.id = 'footerWrapperElement';
        widget.setMountElement(footerWrapperElement);
        widget.mount();
        document.getElementById('global-footer-placeholder')?.appendChild(footerWrapperElement);
      }
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Front-end wp-mx-widgets.js] - generateWidgets', error);
  }
};

const addWidgetHooks = async () => {
  widgets.capture(async (widget: any) => {
    if (widget.getPluginTypeId() === 'global_header') {
      widget.getExtension('citizen_profile.session').then(async (session: any) => {
        session.configure({
          active: stores.UserStore().isAuthenticated,
          endpoints: {
            loginRedirectUrl: '/',
            loginUrl: '/auth/login',
            logoutUrl: '/auth/logout',
            switchCapacityUrl: '/auth/switch'
          }
        });
      });

      widget.on('citizen_profile.session.logout.request', async (logoutRequest) => {
        logoutRequest.acknowledge();
        var reason = logoutRequest.getRequest().getReason();
        switch (reason) {
          case 'user':
            data.isLoggingOut = true;
            logoutRequest.accept();
            break;
          default:
            logoutRequest.reject();
            break;
        }
      });
    }
  });
};
</script>

<style lang="scss" scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex: 1;
}

.logging-out {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px;
}

.vl-loader {
  margin-top: 5px;
  display: block;
}

.loader-container {
}
</style>
